import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Settings, Copy, Edit, Trash2, CheckCircle, Link2, GitBranch } from 'lucide-react';
import { fieldTypeLabels } from './templateConstants';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

/**
 * FieldRow - Displays a single custom merge field in the list
 * Shows field name, tag, type, and action buttons (copy, edit, delete)
 */
export default function FieldRow({ field, onEdit, onDelete, onCopy, copiedField, customFields = [] }) {
  const hasDependency = field.dependency?.depends_on;
  const isChainField = hasDependency && customFields.some(f => 
    f.dependency?.depends_on === field.tag || 
    (f.tag !== field.tag && f.dependency?.depends_on === field.dependency?.depends_on)
  );

  // Get dependency description
  const getDependencyDescription = () => {
    if (!hasDependency) return null;
    const parentField = customFields.find(f => f.tag === field.dependency.depends_on);
    const parentName = parentField?.name || field.dependency.depends_on;
    
    const conditions = {
      'equals': `"${parentName}" = "${field.dependency.value}"`,
      'not_equals': `"${parentName}" ≠ "${field.dependency.value}"`,
      'exists': `"${parentName}" has a value`,
      'not_exists': `"${parentName}" is empty`,
      'contains': `"${parentName}" contains "${field.dependency.value}"`,
      'greater_than': `"${parentName}" > ${field.dependency.value}`,
      'less_than': `"${parentName}" < ${field.dependency.value}`
    };
    
    return conditions[field.dependency.condition] || `Depends on ${parentName}`;
  };

  // Get full dependency chain for tooltip
  const getDependencyChain = () => {
    if (!hasDependency) return [];
    const chain = [];
    let currentTag = field.dependency.depends_on;
    let depth = 0;
    const maxDepth = 10;
    
    while (currentTag && depth < maxDepth) {
      const parentField = customFields.find(f => f.tag === currentTag);
      if (!parentField) break;
      
      chain.unshift({
        name: parentField.name,
        tag: parentField.tag,
        condition: parentField.dependency?.condition,
        value: parentField.dependency?.value
      });
      
      currentTag = parentField.dependency?.depends_on;
      depth++;
    }
    
    return chain;
  };

  const dependencyChain = getDependencyChain();

  return (
    <div 
      className={`flex items-center justify-between p-3 rounded-lg bg-white border hover:shadow-sm transition-shadow ${
        hasDependency ? (dependencyChain.length > 0 ? 'border-l-4 border-l-purple-400' : 'border-l-4 border-l-blue-400') : ''
      }`}
      style={hasDependency && dependencyChain.length > 0 ? { marginLeft: `${Math.min(dependencyChain.length * 8, 32)}px` } : {}}
    >
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded bg-indigo-100 flex items-center justify-center">
          <Settings className="w-4 h-4 text-indigo-600" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">{field.name}</span>
            {field.required && <span className="text-red-500 text-xs">*</span>}
            {/* Dependency badges */}
            {hasDependency && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge 
                      variant="outline" 
                      className={`text-xs cursor-help ${
                        dependencyChain.length > 0 
                          ? 'border-purple-300 text-purple-700 bg-purple-50' 
                          : 'border-blue-300 text-blue-700 bg-blue-50'
                      }`}
                    >
                      {dependencyChain.length > 0 ? (
                        <>
                          <GitBranch className="w-3 h-3 mr-1" />
                          Chain ({dependencyChain.length + 1})
                        </>
                      ) : (
                        <>
                          <Link2 className="w-3 h-3 mr-1" />
                          Conditional
                        </>
                      )}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <div className="text-xs">
                      {dependencyChain.length > 0 && (
                        <div className="mb-2 pb-2 border-b">
                          <p className="font-medium mb-1">Dependency Chain:</p>
                          {dependencyChain.map((item, idx) => (
                            <p key={item.tag} className="text-muted-foreground">
                              {idx + 1}. {item.name}
                            </p>
                          ))}
                          <p>{dependencyChain.length + 1}. {field.name} (this field)</p>
                        </div>
                      )}
                      <p>Shows when: {getDependencyDescription()}</p>
                      {field.dependency?.make_required && (
                        <p className="mt-1 text-amber-600">Required when visible</p>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <code className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-mono">
              {`{{${field.tag}}}`}
            </code>
            <Badge variant="secondary" className="text-xs">
              {fieldTypeLabels[field.field_type] || field.field_type}
            </Badge>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onCopy(`{{${field.tag}}}`)}>
          {copiedField === `{{${field.tag}}}` ? (
            <CheckCircle className="w-4 h-4 text-emerald-500" />
          ) : (
            <Copy className="w-4 h-4" />
          )}
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onEdit}>
          <Edit className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onDelete}>
          <Trash2 className="w-4 h-4 text-red-500" />
        </Button>
      </div>
    </div>
  );
}
